import { useRef, useState, type FC } from "react";
import {
  type Client,
  type ClientShop,
} from "@userFrontend/features/clientReview/components/ClientReviewInputs/type";
import { useSearchModal } from "@userFrontend/features/clientReview/components/ClientReviewInputs/useSearchModal";
import { Presentation } from "./Presentation";
import { type ViolationReportProps, type ViolationReportFormData } from "./type";
import { useViolationReportForm } from "./useViolationReportForm";
import { useFormHandlers } from "./useFormHandlers";
import { submitViolationReport } from "./submitForm";

/**
 * このファイルでは
 * フォーム状態管理とバリデーションを担当し、
 * Presentation コンポーネントに必要な Props を渡す。
 */
export const ViolationReportInputs: FC<ViolationReportProps> = ({
  registeredEmail,
  userContactPath,
  mypageTopPath,
  clientSearchPath,
  violationTypes,
  jobEntryButtons,
  postUrl,
  completedRedirectPath,
}) => {
  // ========== エラー時のスクロールに使用するRef ==========
  const violationCheckboxElement = useRef<HTMLDivElement>(null);
  const otherDetailElement = useRef<HTMLDivElement>(null);
  const clientElement = useRef<HTMLDivElement>(null);
  const personInChargeNameElement = useRef<HTMLDivElement>(null);
  const personInChargeDetailsElement = useRef<HTMLDivElement>(null);
  const documentElement = useRef<HTMLDivElement>(null);
  const emailElement = useRef<HTMLDivElement>(null);

  // ========== Client & Client Shop State ==========
  const [client, setClient] = useState<Client | undefined>(undefined);
  const [clientShop, setClientShop] = useState<ClientShop | undefined>(undefined);
  const [clientErrorText, setClientErrorText] = useState<string | undefined>(undefined);

  // ========== UI State ==========
  const [isShowSearchModal, setIsShowSearchModal] = useState(false);
  const [displaySearchText, setDisplaySearchText] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ========== Job Entry State ==========
  const [checkedJobEntryId, setCheckedJobEntryId] = useState<number | null>(null);

  // ========== Search Modal State ==========
  const {
    isShowClientsDetail,
    clientNameValue,
    shopNameValue,
    searchResults,
    tooManyResultErrorText,
    noResultError,
    isSearching,
    handleClientSearch,
    handleShowTextButton,
    handleChangeClientNameValue,
    handleChangeShopNameValue,
    handleReturnToMyPageTop,
  } = useSearchModal(mypageTopPath, clientSearchPath);

  // ========== React Hook Form Config ==========
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useViolationReportForm(registeredEmail);

  // ========== Form Handlers ==========
  const handlers = useFormHandlers({
    setValue,
    watch,
    clearErrors,
    setClient,
    setClientShop,
    setCheckedJobEntryId,
    setClientErrorText,
    setIsShowSearchModal,
    setDisplaySearchText,
  });

  // ========== Scroll to Error ==========
  const scrollToError = (errorKey: string) => {
    const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
      violationType: violationCheckboxElement,
      violationTypeOtherDetail: otherDetailElement,
      clientId: clientElement,
      jobEntryId: clientElement,
      personInChargeName: personInChargeNameElement,
      personInChargeDetails: personInChargeDetailsElement,
      documentsSubmittable: documentElement,
      alternativeEmail: emailElement,
    };

    const targetRef = refMap[errorKey];
    if (targetRef?.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // ========== Form Submission ==========
  const onSubmit = () => {
    // 送信中の場合は何もしない（連打での連続投稿防止）
    if (isSubmitting) {
      return;
    }

    // 送信処理を開始（連打防止のため、バリデーション前にフラグを立てる）
    setIsSubmitting(true);

    // 「その他」が選択されていない場合、「その他」のテキストエリアの内容をクリア
    const violationTypeValues = watch("violationType") || [];
    const otherOption = violationTypes.find((vt) => vt.optionText.includes("その他"));
    const isOtherSelected = otherOption && violationTypeValues.includes(otherOption.optionId);

    if (!isOtherSelected) {
      setValue("violationTypeOtherDetail", "");
    }

    // フロントバリデーションを実行
    handleSubmit(
      async (data: ViolationReportFormData) => {
        try {
          // clientId と jobEntryId の相互排他性を保証
          // UIの状態（client/checkedJobEntryId）に基づいて最終的な値を決定
          let submitData: ViolationReportFormData = data;

          if (checkedJobEntryId !== null) {
            // ラジオボタンから選択している場合（jobEntryId優先）
            submitData = {
              ...data,
              jobEntryId: checkedJobEntryId,
              clientId: undefined,
              clientShopId: undefined,
            };
          } else if (client) {
            // モーダルから企業を選択している場合（clientId優先）
            submitData = {
              ...data,
              clientId: client.clientId,
              clientShopId: clientShop?.shopId,
              jobEntryId: undefined,
            };
          }

          await submitViolationReport({
            postUrl,
            data: submitData,
            violationTypes,
            registeredEmail,
            completedRedirectPath,
            setServerErrors: (serverErrors) => {
              // サーバーエラーをreact-hook-formのエラーとして設定
              serverErrors.forEach((error) => {
                const serverKey = Object.keys(error)[0];
                const message = error[serverKey];

                // Rails側のキーをフロントエンドのフィールド名に変換
                const keyMap: Record<string, keyof ViolationReportFormData> = {
                  violation: "violationType",
                  violationOtherDetail: "violationTypeOtherDetail",
                  client: "clientId",
                  clientId: "clientId",
                  jobEntryId: "jobEntryId",
                  personInCharge: "personInChargeName",
                  personInChargeDetails: "personInChargeDetails",
                  email: "alternativeEmail",
                  alternativeEmail: "alternativeEmail",
                  documentsSubmittable: "documentsSubmittable",
                };
                const mappedKey = keyMap[serverKey] || (serverKey as keyof ViolationReportFormData);

                if (mappedKey && message) {
                  setError(mappedKey, {
                    type: "server",
                    message,
                  });
                }
              });
            },
            scrollToError,
          });
        } finally {
          setIsSubmitting(false);
        }
      },
      (validationErrors) => {
        // バリデーションエラーが発生した場合、送信フラグをリセット
        setIsSubmitting(false);

        let targetElement: HTMLElement | null = null;

        if (validationErrors.violationType) {
          targetElement = violationCheckboxElement.current;
        } else if (validationErrors.violationTypeOtherDetail) {
          targetElement = otherDetailElement.current;
        } else if (validationErrors.clientId) {
          targetElement = clientElement.current;
        } else if (validationErrors.personInChargeName) {
          targetElement = personInChargeNameElement.current;
        } else if (validationErrors.personInChargeDetails) {
          targetElement = personInChargeDetailsElement.current;
        }

        if (targetElement) {
          // 要素の位置を取得してスクロール
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 100; // 要素ぴったりすぎて見づらいため、-100pxの余白を追加

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      },
    )();
  };

  return (
    <Presentation
      violationTypeProps={{
        questions: {
          violationTypes,
        },
        register,
        watch,
        setValue,
        errors,
        checkboxRef: violationCheckboxElement,
        otherDetailRef: otherDetailElement,
      }}
      clientSelectSectionProps={{
        title: "企業情報",
        subtitle: "該当する応募先",
        isShowAnnotation: false,
        clientRef: clientElement,
        client,
        clientShop,
        isShowSearchModal,
        handleShowModal: () => setIsShowSearchModal(true),
        handleCloseModal: () => setIsShowSearchModal(false),
        clientNameValue,
        shopNameValue,
        clientNameErrorText: "",
        shopNameErrorText: "",
        handleClientSearch,
        handleChangeClientNameValue,
        handleChangeShopNameValue,
        searchResults,
        handleClientSelect: handlers.handleClientSelectWithForm,
        isShowClientsDetail,
        handleShowTextButton,
        tooManyResultErrorText,
        noResultError,
        displaySearchText,
        isSearching,
        handleReturnToMyPageTop,
        userContactPath,
        checkedJobEntryId,
        jobEntryButtons,
        clientErrorText:
          errors.clientId?.message || errors.jobEntryId?.message || clientErrorText || "",
      }}
      documentSubmissionCheckProps={{
        watch,
        setValue,
        containerRef: documentElement,
      }}
      emailConfirmationProps={{
        register,
        watch,
        errors,
        containerRef: emailElement,
      }}
      personInChargeProps={{
        register,
        watch,
        setValue,
        errors,
        nameRef: personInChargeNameElement,
        detailsRef: personInChargeDetailsElement,
      }}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    />
  );
};
